import { TRAINING_STATUSES } from '@/features/trainings/constants';

/**
 * Parse "X/Y" from a step_name like "SpecificAI: 5/11 records".
 * Returns { processed, total } or null.
 */
function parseStepNameCounts(stepName) {
  if (!stepName) return null;
  const match = stepName.match(/(\d+)\/(\d+)/);
  if (!match) return null;
  return { processed: parseInt(match[1], 10), total: parseInt(match[2], 10) };
}

/**
 * Calculate LLM-as-a-Judge progress from evaluation sub-task data.
 *
 * percentage = (processed_benchmark + processed_specificAI) / (total_benchmark + total_specificAI) * 100
 *
 * @param {Object} evaluationProgress - Global evaluation progress keyed by "{usecaseId}_{version}_{subTaskType}"
 * @param {string} usecaseId
 * @param {number|string} version
 * @returns {number|null} 0–100 percentage, or null if sub-task data is unavailable
 */
export function calculateLlmJudgeProgress(evaluationProgress, usecaseId, version) {
  if (!evaluationProgress || !usecaseId || version == null) return null;

  const internal = parseStepNameCounts(evaluationProgress[`${usecaseId}_${version}_internal`]?.step_name);
  const benchmark = parseStepNameCounts(evaluationProgress[`${usecaseId}_${version}_benchmark`]?.step_name);

  if (!internal && !benchmark) return null;

  const processed = (internal?.processed || 0) + (benchmark?.processed || 0);
  const total = (internal?.total || 0) + (benchmark?.total || 0);

  if (total === 0) return null;
  return Math.round((processed / total) * 100);
}

/**
 * Return the effective progress value for a training status entry.
 * During LLM_AS_A_JUDGE, overrides backend progress with the frontend-calculated
 * percentage derived from internal + benchmark sub-task counts.
 */
export function getEffectiveProgress(status, backendProgress, evaluationProgress, usecaseId, version) {
  if (status !== TRAINING_STATUSES.LLM_AS_A_JUDGE) return backendProgress;
  return calculateLlmJudgeProgress(evaluationProgress, usecaseId, version) ?? backendProgress;
}
